import { useEffect, useState } from 'react';
import { Minus, Plus, Users } from 'lucide-react';
import { reservationsApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Notice } from '@verlo/shared/components/ui/notice';
import { OptionChips } from '@verlo/shared/components/ui/segmented';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@verlo/shared/components/ui/dialog';

// Seat a walk-in (Oct 2026 sweep): how many, for how long, then the tables that are free now
// and stay free long enough, best fit first. The same check as a booking's "Find a table",
// without making a booking. Picking a table opens the usual seat sheet with the guests filled
// in, so the allergy and occasion questions are asked the same way every time.

const LENGTHS = [60, 90, 120];
const lengthFor = (n) => (n <= 2 ? 60 : 90);

export default function QuickSeatDialog({ open, onOpenChange, onPick }) {
  const [covers, setCovers] = useState(2);
  const [minutes, setMinutes] = useState(60);
  const [touchedLength, setTouchedLength] = useState(false);
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (open) { setCovers(2); setMinutes(60); setTouchedLength(false); }
  }, [open]);

  // The length follows the party (60 for two, 90 for more) until someone picks one.
  const changeCovers = (n) => {
    const next = Math.max(1, Math.min(30, n));
    setCovers(next);
    if (!touchedLength) setMinutes(lengthFor(next));
  };

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setFailed(false);
    const t = setTimeout(() => {
      reservationsApi.freeTables(covers, minutes)
        .then((r) => { if (live) setRows(r.data.tables || []); })
        .catch(() => { if (live) { setRows([]); setFailed(true); } });
    }, 150);
    return () => { live = false; clearTimeout(t); };
  }, [open, covers, minutes]);

  const fits = (rows || []).filter((r) => r.fits);
  const short = (rows || []).filter((r) => !r.fits);
  const best = fits[0];

  const why = (r) => {
    const bits = [`${r.capacity} seats`, r.section];
    bits.push(r.next_booking ? `booked ${r.next_booking}` : 'free all evening');
    return bits.join(' · ');
  };

  const Row = ({ r, tag }) => (
    <button
      type="button"
      onClick={() => onPick(r, covers, r.fits ? minutes : Math.min(minutes, r.free_minutes))}
      className={`flex min-h-12 w-full items-center gap-3 border px-3 py-2 text-left transition-colors hover:border-verlo ${
        r === best ? 'border-verlo bg-verlo-tint ring-1 ring-inset ring-verlo' : 'border-hairline bg-white'}`}
      data-testid={`quick-seat-${r.name}`}
    >
      <span className="w-12 font-mono text-[15px] font-bold text-ink">{r.name}</span>
      <span className="flex-1 text-[13px] text-ink-soft">{why(r)}</span>
      <span className="font-heading text-[12px] font-semibold text-verlo">{tag}</span>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="form" data-testid="quick-seat-dialog">
        <DialogHeader>
          <DialogTitle>Seat a walk-in</DialogTitle>
          <DialogDescription>Pick a table and the seat sheet opens with the guests filled in.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div className="text-sm font-medium">How many</div>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => changeCovers(covers - 1)} disabled={covers <= 1} aria-label="One fewer">
              <Minus size={16} />
            </Button>
            <span className="w-10 text-center font-heading text-2xl font-extrabold tabular-nums" data-testid="quick-seat-covers">{covers}</span>
            <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => changeCovers(covers + 1)} aria-label="One more">
              <Plus size={16} />
            </Button>
            <OptionChips value={covers} onChange={changeCovers}
              options={[2, 4, 6].map((n) => ({ value: n, label: String(n), testId: `quick-seat-${n}-guests` }))} chipClassName="h-10 w-12" />
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">For how long</div>
          <OptionChips value={minutes} onChange={(m) => { setMinutes(m); setTouchedLength(true); }}
            options={LENGTHS.map((m) => ({ value: m, label: m < 120 ? `${m} min` : '2 hours', testId: `quick-seat-${m}-min` }))} chipClassName="h-10" />
        </div>

        <div className="space-y-2" aria-live="polite">
          <div className="text-sm font-medium">Free now, for {minutes < 120 ? `${minutes} minutes` : '2 hours'}</div>
          {rows == null ? (
            <div className="h-12 animate-pulse bg-surface-2" />
          ) : failed ? (
            <Notice tone="stop" word="Offline">Couldn't check the tables. Try again, or seat from the floor.</Notice>
          ) : rows.length === 0 ? (
            <Notice tone="warn" word="Full" data-testid="quick-seat-none">
              No free table seats {covers} now. Check Bookings for one about to finish.
            </Notice>
          ) : (
            <div className="grid max-h-72 gap-1.5 overflow-y-auto" data-testid="quick-seat-list">
              {fits.length === 0 && (
                <Notice tone="warn" word="Short">
                  No table for {covers} for {minutes} minutes. These are free for less.
                </Notice>
              )}
              {fits.map((r) => <Row key={r.id} r={r} tag={r === best ? 'Best fit' : r.capacity >= covers + 3 ? `Big for ${covers}` : 'Fits'} />)}
              {short.map((r) => <Row key={r.id} r={r} tag={`Free for ${r.free_minutes}`} />)}
            </div>
          )}
        </div>
        <p className="flex items-center gap-1.5 text-[12px] text-ink-faint"><Users size={12} /> Tables held for a booking in the next 15 minutes aren't offered.</p>
      </DialogContent>
    </Dialog>
  );
}
